import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User as UserIcon, Mail, Briefcase, Users, ArrowLeft, Save, Loader2, Trophy, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getUserData, setUserData, usersAPI } from "@/lib/api";
import { useProfile } from "@/hooks/use-profile";
import { useStrengths } from "@/hooks/use-strengths";
import UploadReportSection from "@/components/UploadReportSection";
import { translateDomainName, translateStrengthName, getDomainForStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface UserProfile {
  full_name?: string;
  email?: string;
  bio?: string;
  job_title?: string;
  company?: string;
  interests?: string[];
  avatar_url?: string;
}

interface CliftonStrength {
  rank: number;
  theme_name: string;
}

interface CliftonStrengthsState {
  status: "success" | "no_report" | "pending" | "needs_review" | "failed";
  message?: string;
  strengths: CliftonStrength[];
}

export default function Profile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentUser = getUserData();
  const { updateProfileVisibility, isLoading: visibilityLoading } = useProfile();
  const { getMyCliftonStrengths } = useStrengths();

  const [isPublic, setIsPublic] = useState(
    currentUser?.isProfilePublic || false
  );

  const [loading, setLoading] = useState(false);
  const [strengthsLoading, setStrengthsLoading] = useState(true);
  const [cliftonStrengths, setCliftonStrengths] = useState<CliftonStrengthsState>({
    status: "no_report",
    strengths: [],
    message: undefined,
  });
  const [profile, setProfile] = useState<UserProfile>({
    full_name: currentUser?.name || "",
    email: currentUser?.email || "",
    bio: currentUser?.bio || "",
    job_title: currentUser?.jobTitle || "",
    company: currentUser?.company || "",
    interests: currentUser?.interests || [],
  });
  const [newInterest, setNewInterest] = useState("");

  // Fetch initial profile data only once when the component mounts
  useEffect(() => {
    const user = getUserData();
    if (user) {
      setProfile({
        full_name: user.name || "",
        email: user.email || "",
        bio: user.bio || "",
        job_title: user.jobTitle || "",
        company: user.company || "",
        interests: user.interests || [],
      });
    }
  }, []);

  const loadCliftonStrengths = async () => {
    setStrengthsLoading(true);
    const result = await getMyCliftonStrengths();

    if (result.success && result.data) {
      setCliftonStrengths({
        status: result.data.status,
        strengths: result.data.strengths || [],
        message: result.data.message,
      });
    } else {
      setCliftonStrengths({
        status: "failed",
        strengths: [],
        message: t("errors.cliftonLoadFailed"),
      });
    }

    setStrengthsLoading(false);
  };

  useEffect(() => {
    loadCliftonStrengths();
  }, []);

  const handleSave = async () => {
    if (!profile.full_name || !profile.email) {
      toast.error(t('profile.errorTitle'), {
        description: t("errors.fillRequiredFields"),
      });
      return;
    }

    setLoading(true);
    try {
      // Call the backend API
      const response = await usersAPI.updateProfile({
        name: profile.full_name,
        bio: profile.bio,
        jobTitle: profile.job_title,
        company: profile.company,
        interests: profile.interests,
      });

      if (response.success) {
        // Update local storage user data to keep it in sync
        const updatedUser = { ...currentUser, ...response.data };
        setUserData(updatedUser);

        toast.success(t('profile.successTitle'), {
          description: t('profile.profileUpdated'),
        });
      }
    } catch (error) {
      toast.error(t('profile.errorTitle'), {
        description: t('profile.updateFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  const addInterest = () => {
    if (newInterest.trim() && !profile.interests?.includes(newInterest.trim())) {
      setProfile({
        ...profile,
        interests: [...(profile.interests || []), newInterest.trim()]
      });
      setNewInterest("");
    }
  };

  const removeInterest = (interest: string) => {
    setProfile({
      ...profile,
      interests: profile.interests?.filter(i => i !== interest) || []
    });
  };

  const handleToggleVisibility = async () => {
    const newValue = !isPublic;
    setIsPublic(newValue);

    const res = await updateProfileVisibility(newValue);

    if (res.success) {
      // update localStorage
      const user = getUserData();
      user.isProfilePublic = newValue;
      setUserData(user);
    } else {
      // rollback if failed
      setIsPublic(!newValue);
    }
  };

  const getStrengthsStatusMessage = () => {
    if (cliftonStrengths.message) {
      if (cliftonStrengths.message === "No results yet") {
        return t("profile.noStrengthsYet");
      }
      return cliftonStrengths.message;
    }

    switch (cliftonStrengths.status) {
      case "pending":
        return t("profile.cliftonProcessing");
      case "needs_review":
        return t("profile.cliftonNeedsReview");
      case "failed":
        return getLocalizedErrorMessage("Your Clifton Strengths report extraction failed", "errors.pdfExtractFailed");
      default:
        return t("profile.noStrengthsYet");
    }
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-gradient-to-b from-background to-secondary/20 pt-20 pb-10 sm:pt-24 sm:pb-12"
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
    >
      <div className="container mx-auto max-w-4xl px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
            <Avatar className="h-20 w-20 sm:h-24 sm:w-24">
              <AvatarImage
                src={currentUser?.profileImageDisplayUrl || currentUser?.profileImage || undefined}
                alt={profile.full_name || currentUser?.name || t('profile.title')}
              />
              <AvatarFallback className="bg-primary text-primary-foreground text-3xl">
                {profile.full_name?.charAt(0).toUpperCase() || currentUser?.name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="text-3xl font-bold mb-2">{t('profile.title')}</h1>
              <p className="text-muted-foreground">{t('profile.updateInformation')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                {t('profile.basicInfo')}
              </CardTitle>
              <CardDescription>{t('profile.basicInfoDesc') || "Manage your basic information"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-foreground font-semibold">
                  {t('profile.fullName')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="fullName"
                  value={profile.full_name || ""}
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  placeholder={t('profile.fullNamePlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground font-semibold">
                  {t('profile.email')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email || ""}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  placeholder={t('profile.emailPlaceholder')}
                  disabled
                />
                <p className="text-xs text-muted-foreground">
                  {t('profile.emailNote') || "Email cannot be changed"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio" className="text-foreground font-semibold">
                  {t('profile.bio')} <span className="text-muted-foreground text-xs">({t('common.optional')})</span>
                </Label>
                <Textarea
                  id="bio"
                  value={profile.bio || ""}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder={t('profile.bioPlaceholder')}
                  className="min-h-[100px]"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  {profile.bio?.length || 0}/500 {t('profile.characters') || "characters"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-secondary" />
                {t('profile.professionalInfo')}
              </CardTitle>
              <CardDescription>{t('profile.professionalInfoDesc') || "Tell us about your professional background"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="jobTitle" className="text-foreground font-semibold">
                  {t('profile.jobTitle')}
                </Label>
                <Input
                  id="jobTitle"
                  value={profile.job_title || ""}
                  onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                  placeholder={t('profile.jobTitlePlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-foreground font-semibold">
                  {t('profile.company')}
                </Label>
                <Input
                  id="company"
                  value={profile.company || ""}
                  onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                  placeholder={t('profile.companyPlaceholder')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Interests */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                {t('profile.interests')}
              </CardTitle>
              <CardDescription>{t('profile.interestsDesc') || "Share your interests and hobbies"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="interests" className="text-foreground font-semibold">
                  {t('profile.addInterests')}
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="interests"
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addInterest()}
                    placeholder={t('profile.interestsPlaceholder')}
                  />
                  <Button
                    type="button"
                    onClick={addInterest}
                    variant="outline"
                    className="w-full whitespace-nowrap sm:w-auto"
                  >
                    {t('profile.add') || "Add"}
                  </Button>
                </div>
              </div>

              {profile.interests && profile.interests.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive/20"
                      onClick={() => removeInterest(interest)}
                    >
                      {interest} ×
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clifton Strengths PDF */}
          <UploadReportSection onDataExtracted={loadCliftonStrengths} />

          {/* My Clifton Strengths */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-primary" />
                {t("profile.myCliftonStrengths")}
              </CardTitle>
              <CardDescription>
                {t("profile.cliftonStrengthsDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {strengthsLoading ? (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t("profile.loadingCliftonStrengths")}
                </div>
              ) : cliftonStrengths.status === "success" && cliftonStrengths.strengths.length === 34 ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[...cliftonStrengths.strengths]
                    .sort((a, b) => a.rank - b.rank)
                    .map((strength) => {
                      const domain = getDomainForStrengthName(strength.theme_name);
                      const color = domain ? STRENGTHS_EXPLORER_DOMAIN_COLORS[domain] : undefined;
                      return (
                        <div
                          key={strength.rank}
                          className="flex min-w-0 items-center gap-3 rounded-md border border-s-4 p-3"
                          style={color ? { borderInlineStartColor: color, backgroundColor: `${color}0d` } : undefined}
                        >
                          <div
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold"
                            style={color ? { backgroundColor: `${color}20`, color } : undefined}
                          >
                            {strength.rank}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="min-w-0 break-words font-medium text-foreground">
                              {translateStrengthName(strength.theme_name, t, i18n.language)}
                            </p>
                            {domain && (
                              <Badge
                                variant="outline"
                                className="mt-1 border text-xs"
                                style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                              >
                                {translateDomainName(domain, t, i18n.language)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-md border border-dashed border-border bg-muted/20 p-4">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">{getStrengthsStatusMessage()}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("profile.cliftonUploadHint")}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Public Profile */}
          <Card>
            <CardHeader>
              <CardTitle>{t("profile.publicProfile")}</CardTitle>
              <CardDescription>
                {t("profile.publicProfileDesc")}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium">
                    {isPublic ? t("profile.public") : t("profile.private")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isPublic
                      ? t("profile.publicDesc")
                      : t("profile.privateDesc")}
                  </p>
                </div>

                <Button
                  variant={isPublic ? "default" : "outline"}
                  onClick={handleToggleVisibility}
                  disabled={visibilityLoading}
                  className="w-full sm:w-auto"
                >
                  {visibilityLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isPublic ? (
                    t("profile.makePrivate")
                  ) : (
                    t("profile.makePublic")
                  )}
                </Button>
              </div>

              {isPublic && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    className="min-w-0"
                    readOnly
                    value={`${window.location.origin}/profile/${currentUser?._id}/strengths`}
                  />
                  <Button
                    variant="secondary"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}/profile/${currentUser?._id}/strengths`
                      );
                      toast.success(t("profile.linkCopied"));
                    }}
                  >
                    {t("profile.copy")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="sticky bottom-4 flex flex-col-reverse gap-3 rounded-lg bg-background/80 p-2 backdrop-blur sm:bottom-6 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !profile.full_name || !profile.email}
              className="w-full bg-primary hover:bg-primary/90 sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('profile.saving') || "Saving..."}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('profile.saveProfile') || "Save Profile"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
